import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { c } from '../theme';
import { Wordmark } from './Brand';
import type { ReportSummary } from '../api';

/**
 * Per-report actions behind the ••• button, using the same bottom-sheet
 * pattern as the main menu.
 */
export function ReportActions({
  report,
  onClose,
  onOpen,
  onArchive,
  busy,
}: {
  report: ReportSummary;
  onClose: () => void;
  onOpen: () => void;
  onArchive: () => void;
  busy?: string | null;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const items: { label: string; sub: string; dot: string; run: () => void; key: string }[] = [
    { key: 'open', label: 'Open report', sub: report.verdict ?? 'View the full overview', dot: c.red, run: onOpen },
    {
      key: 'archive',
      label: report.archived ? 'Restore' : 'Archive',
      sub: report.archived ? 'Move back into Delivered' : 'Hide from Delivered, keep the report',
      dot: c.violet,
      run: onArchive,
    },
  ];

  // Portalled to <body> so no layer on the page can draw over the sheet.
  return createPortal(
    <div
      onClick={onClose}
      className="rb-sheet-wrap"
      role="dialog"
      aria-modal="true"
      aria-label={`Actions for ${report.line1}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(31,26,31,0.45)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        animation: 'fadeIn .2s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rb-sheet"
        style={{
          width: 390,
          maxWidth: '100%',
          boxSizing: 'border-box',
          background: c.screen,
          borderRadius: '40px 40px 0 0',
          padding: '16px var(--gx) 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 22,
          maxHeight: '100vh',
          overflowY: 'auto',
          animation: 'menuUp .28s ease-out',
        }}
      >
        <div style={{ width: 36, height: 4, borderRadius: 999, background: 'rgba(31,26,31,0.15)', alignSelf: 'center' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <Wordmark />
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: `1px solid ${c.ink18}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: c.ink,
              flex: 'none',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
              <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em' }}>{report.line1.toUpperCase()}</div>
          <div style={{ fontSize: 12, color: c.ink5 }}>{report.locality.toUpperCase()}</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {items.map((it) => (
            <button
              key={it.key}
              className="rb-row"
              disabled={!!busy}
              onClick={it.run}
              style={{
                display: 'grid',
                gridTemplateColumns: '26px 1fr auto',
                alignItems: 'center',
                gap: 14,
                padding: '15px 0',
                borderTop: `1px solid ${c.hairlineList}`,
                color: c.ink,
                textAlign: 'left',
                opacity: busy && busy !== it.key ? 0.4 : 1,
                cursor: busy ? 'default' : 'pointer',
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: c.chip,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: it.dot }} />
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 15, fontWeight: 500 }}>{it.label}</span>
                <span style={{ fontSize: 11, color: c.ink5, lineHeight: 1.4 }}>{it.sub}</span>
              </span>
              <span style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 600, color: c.red, whiteSpace: 'nowrap' }}>
                {busy === it.key ? 'WORKING…' : ''}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  ,
    document.body,
  );
}
